/**
 * GenericForm — renders any RemoteCommandCatalogEntry whose top-level fields
 * are scalar (string / int / number / datetime / bool / enum / enum_multi /
 * url / table). Two actions in v1 bypass this in favour of custom forms:
 *   - setChargingProfile  (nested CS charging profile object)
 *   - sendLocalList       (table of id_tag entries with nested id_tag_info)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import type {
  RemoteCommandCatalogEntry,
  RemoteCommandField,
} from "../../../../api/maintenance";

interface GenericFormProps {
  entry: RemoteCommandCatalogEntry;
  onSubmit: (meta: Record<string, unknown>) => Promise<void> | void;
  submitting: boolean;
  defaults?: Record<string, unknown>;
}

type FieldErrors = Record<string, string>;

const datetimeLocalToISO = (v: string): string | undefined => {
  if (!v) return undefined;
  // value comes as "YYYY-MM-DDTHH:mm" in local time; convert to ISO 8601 with TZ
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const isoToDatetimeLocal = (v: string | undefined | null): string => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function buildInitial(entry: RemoteCommandCatalogEntry, defaults?: Record<string, unknown>) {
  const out: Record<string, unknown> = {};
  for (const f of entry.fields) {
    const fromDefaults = defaults?.[f.name];
    if (fromDefaults !== undefined) {
      // datetime: API gives ISO, but the input wants datetime-local format
      out[f.name] = f.type === "datetime" ? isoToDatetimeLocal(fromDefaults as string) : fromDefaults;
    } else if (f.default !== undefined && f.default !== null) {
      out[f.name] = f.default;
    } else if (f.type === "enum_multi" || f.type === "table") {
      out[f.name] = [];
    } else if (f.type === "bool") {
      out[f.name] = false;
    } else {
      out[f.name] = "";
    }
  }
  return out;
}

function validate(entry: RemoteCommandCatalogEntry, values: Record<string, unknown>): FieldErrors {
  const errors: FieldErrors = {};
  for (const f of entry.fields) {
    const raw = values[f.name];
    const empty =
      raw === undefined || raw === null || raw === "" ||
      (Array.isArray(raw) && raw.length === 0);

    if (f.required && empty) {
      errors[f.name] = `${f.label} is required`;
      continue;
    }
    if (empty) continue;

    if (f.type === "int" || f.type === "number") {
      const num = Number(raw);
      if (Number.isNaN(num)) {
        errors[f.name] = `${f.label} must be a number`;
        continue;
      }
      if (f.type === "int" && !Number.isInteger(num)) {
        errors[f.name] = `${f.label} must be an integer`;
        continue;
      }
      if (f.min != null && num < f.min) {
        errors[f.name] = `${f.label} must be ≥ ${f.min}`;
        continue;
      }
      if (f.max != null && num > f.max) {
        errors[f.name] = `${f.label} must be ≤ ${f.max}`;
        continue;
      }
    } else if (f.type === "url") {
      try {
        // eslint-disable-next-line no-new
        new URL(String(raw));
      } catch {
        errors[f.name] = `${f.label} must be a valid URL`;
      }
    } else if (f.type === "datetime") {
      const iso = datetimeLocalToISO(String(raw));
      if (!iso) errors[f.name] = `${f.label} must be a valid datetime`;
    } else if (f.type === "string") {
      const s = String(raw);
      if (f.max != null && s.length > f.max) {
        errors[f.name] = `${f.label} must be ≤ ${f.max} characters`;
      }
    }
  }
  return errors;
}

function normalise(entry: RemoteCommandCatalogEntry, values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of entry.fields) {
    const raw = values[f.name];
    const empty =
      raw === undefined || raw === null || raw === "" ||
      (Array.isArray(raw) && raw.length === 0 && !f.required);
    if (empty && !f.required) continue;

    if (f.type === "int" || f.type === "number") {
      out[f.name] = Number(raw);
    } else if (f.type === "datetime") {
      out[f.name] = datetimeLocalToISO(String(raw));
    } else if (f.type === "bool") {
      out[f.name] = Boolean(raw);
    } else if (f.type === "enum_multi") {
      out[f.name] = Array.isArray(raw) ? raw : [];
    } else if (f.type === "table") {
      // Table editor (for nested entries) — pass through as-is, sub-form handles normalisation.
      out[f.name] = raw;
    } else {
      out[f.name] = raw;
    }
  }
  return out;
}

const ChipInput: React.FC<{
  values: string[];
  options?: string[] | null;
  onChange: (v: string[]) => void;
  placeholder?: string;
}> = ({ values, options, onChange, placeholder }) => {
  const [draft, setDraft] = useState("");
  const remove = (v: string) => onChange(values.filter((x) => x !== v));
  const add = (v: string) => {
    const t = v.trim();
    if (!t || values.includes(t)) return;
    onChange([...values, t]);
    setDraft("");
  };
  const suggestions = useMemo(() => {
    if (!options) return [];
    const q = draft.toLowerCase();
    return options.filter((o) => !values.includes(o) && (!q || o.toLowerCase().includes(q)));
  }, [draft, options, values]);

  return (
    <div className="rcmd-chip-input">
      {values.map((v) => (
        <span key={v} className="rcmd-chip">
          {v}
          <button type="button" className="rcmd-chip-remove" onClick={() => remove(v)}>
            <X size={10} />
          </button>
        </span>
      ))}
      {options ? (
        <select
          value=""
          onChange={(e) => add(e.target.value)}
          style={{ flex: 1, minWidth: 100, border: "none", outline: "none", fontSize: 12 }}
        >
          <option value="">{placeholder ?? "Add…"}</option>
          {suggestions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : (
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            } else if (e.key === "Backspace" && !draft && values.length) {
              remove(values[values.length - 1]);
            }
          }}
          placeholder={placeholder ?? "Type and press Enter"}
        />
      )}
    </div>
  );
};

const GenericForm: React.FC<GenericFormProps> = ({ entry, onSubmit, submitting, defaults }) => {
  const [values, setValues] = useState<Record<string, unknown>>(() => buildInitial(entry, defaults));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [confirm, setConfirm] = useState("");

  // Reset values when switching actions or applying a defaults snapshot.
  useEffect(() => {
    setValues(buildInitial(entry, defaults));
    setErrors({});
    setConfirm("");
  }, [entry.action, defaults]);

  const setField = (name: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const fieldErrors = validate(entry, values);
      if (Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
        return;
      }
      setErrors({});
      const meta = normalise(entry, values);
      await onSubmit(meta);
    },
    [entry, values, onSubmit],
  );

  const renderField = (f: RemoteCommandField) => {
    const id = `rcmd-${entry.action}-${f.name}`;
    const error = errors[f.name];
    const value = values[f.name];

    const labelEl = (
      <>
        {f.label}
        {f.required && <span className="rcmd-field-required">*</span>}
      </>
    );

    const helpEl = f.help && <span className="rcmd-field-help">{f.help}</span>;
    const errorEl = error && <span className="rcmd-field-help" style={{ color: "#c62828" }}>{error}</span>;

    const fullWidth = f.type === "enum_multi" || f.type === "table" || f.type === "json";

    let input: React.ReactNode;
    switch (f.type) {
      case "enum":
        input = (
          <select
            id={id}
            value={(value as string) ?? ""}
            onChange={(e) => setField(f.name, e.target.value)}
            required={f.required}
          >
            {!f.required && <option value="">— none —</option>}
            {f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
        break;
      case "enum_multi":
        input = (
          <ChipInput
            values={(value as string[]) ?? []}
            options={f.options ?? undefined}
            onChange={(v) => setField(f.name, v)}
            placeholder={f.placeholder ?? "Add key…"}
          />
        );
        break;
      case "bool":
        input = (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => setField(f.name, e.target.checked)}
            />
            <span style={{ fontSize: 12, color: "#666" }}>{f.help ?? "Enable"}</span>
          </label>
        );
        break;
      case "int":
      case "number":
        input = (
          <input
            id={id}
            type="number"
            step={f.type === "int" ? 1 : "any"}
            min={f.min ?? undefined}
            max={f.max ?? undefined}
            value={value === "" || value == null ? "" : String(value)}
            onChange={(e) => setField(f.name, e.target.value)}
            placeholder={f.placeholder ?? ""}
            required={f.required}
          />
        );
        break;
      case "datetime":
        input = (
          <input
            id={id}
            type="datetime-local"
            value={(value as string) ?? ""}
            onChange={(e) => setField(f.name, e.target.value)}
            required={f.required}
          />
        );
        break;
      case "url":
        input = (
          <input
            id={id}
            type="url"
            value={(value as string) ?? ""}
            onChange={(e) => setField(f.name, e.target.value)}
            placeholder={f.placeholder ?? "https://…"}
            required={f.required}
          />
        );
        break;
      case "json":
        input = (
          <textarea
            id={id}
            value={typeof value === "string" ? value : JSON.stringify(value ?? {}, null, 2)}
            onChange={(e) => {
              try {
                setField(f.name, JSON.parse(e.target.value));
              } catch {
                setField(f.name, e.target.value);
              }
            }}
            placeholder={f.placeholder ?? "{ ... }"}
            rows={4}
          />
        );
        break;
      default: {
        // string with optional autocomplete suggestions via <datalist>
        const listId = `${id}-options`;
        input = (
          <>
            <input
              id={id}
              type="text"
              value={(value as string) ?? ""}
              onChange={(e) => setField(f.name, e.target.value)}
              placeholder={f.placeholder ?? ""}
              list={f.suggestions ? listId : undefined}
              maxLength={f.max ?? undefined}
              required={f.required}
            />
            {f.suggestions && (
              <datalist id={listId}>
                {f.suggestions.map((s) => <option key={s} value={s} />)}
              </datalist>
            )}
          </>
        );
      }
    }

    return (
      <label
        key={f.name}
        htmlFor={id}
        className={`rcmd-field ${fullWidth ? "rcmd-field-fullwidth" : ""}`}
      >
        {labelEl}
        {input}
        {errorEl || helpEl}
      </label>
    );
  };

  const hasFields = entry.fields.length > 0;
  const confirmNeeded = entry.destructive && entry.confirm_text;
  const confirmOk = !confirmNeeded || confirm.trim() === entry.confirm_text;

  return (
    <form className="rcmd-form" onSubmit={handleSubmit}>
      {entry.description && <p className="rcmd-form-description">{entry.description}</p>}

      {hasFields ? (
        <div className="rcmd-form-grid">{entry.fields.map(renderField)}</div>
      ) : (
        <p style={{ fontSize: 12, color: "#888", margin: 0 }}>No parameters — click Send.</p>
      )}

      <div className="rcmd-form-actions">
        {confirmNeeded && (
          <input
            className="rcmd-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={`Type "${entry.confirm_text}" to confirm`}
          />
        )}
        <button type="submit" className="maint-btn-primary" disabled={submitting || !confirmOk}>
          {submitting ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
};

export default GenericForm;
